import { normalizePhone } from "./phone.js";
/**
 * The number a `wa.me` link needs: digits only, country code included, no plus.
 *
 * ## Why this is its own function
 *
 * The CRM built its WhatsApp links by stripping non-digits off whatever was on
 * the record:
 *
 * ```
 * href={`https://wa.me/${phone.replace(/[^\d]/g, "")}`}
 * ```
 *
 * A person is stored with the number as somebody typed it — `050-866-6333` —
 * because that is how it should be *displayed*. Strip the punctuation off that
 * and you get `0508666333`, which WhatsApp reads as a number with no country
 * code and refuses: "לא ניתן לבדוק את מספר הטלפון 0508666333 מפני שחסרה בו
 * קידומת חיוג". The product owner hit exactly that.
 *
 * The record already holds a normalised form, and `normalizePhone` already knows
 * that a leading zero means Israel. Neither was reached, because the link was
 * built from the display value. So the conversion lives here, next to the rule
 * it depends on, and every link goes through it rather than each screen
 * inventing its own `replace`.
 *
 * ## What it refuses
 *
 * A number too short to be a real one comes back `null` rather than as a link
 * that opens WhatsApp on nothing. A dead control is better than one that leads
 * somewhere wrong — and it lets the caller grey the button out honestly.
 */
/** Shortest plausible international number: country code plus a subscriber. */
const MIN_DIGITS = 8;
export function whatsappNumber(raw) {
    if (typeof raw !== "string" || raw.trim() === "")
        return null;
    const digits = normalizePhone(raw).replace(/[^\d]/g, "");
    if (digits.length < MIN_DIGITS)
        return null;
    // A local Israeli number that lost its zero on the way in — "50866633" — would
    // normalise to a plus and eight digits and still be wrong. Only accept what
    // begins with a country code we can name, or what is long enough to carry one.
    if (digits.length < 10)
        return null;
    return digits;
}
/** The full link, or null where there is no number worth linking to. */
export function whatsappLink(raw, text) {
    const n = whatsappNumber(raw);
    if (!n)
        return null;
    return text ? `https://wa.me/${n}?text=${encodeURIComponent(text)}` : `https://wa.me/${n}`;
}
/**
 * The number for a `tel:` link.
 *
 * Kept separate from WhatsApp on purpose: a phone dialler is perfectly happy
 * with the local form and a person reading the screen recognises it, so there is
 * nothing to gain from rewriting it — but it must still be reachable, so a
 * number that normalises to nothing usable returns null here too.
 */
export function telLink(raw) {
    if (typeof raw !== "string" || raw.trim() === "")
        return null;
    const digits = raw.replace(/[^\d+]/g, "");
    return digits.replace(/\D/g, "").length >= 7 ? `tel:${digits}` : null;
}
//# sourceMappingURL=whatsapp.js.map